'use strict';

const CONFIG = {
  feeTiers: window.CATERING_RUSH_CONFIG.feeTiers, // [{ amountCents, variantId }, ...]

  cateringProductType: 'Catering',

  flatFeeAmountCents: 1500,     // $15 — used for sub-$100 orders
  flatFeeThresholdCents: 10000, // $100

  percentTiers: [
    { minHours: 48, maxHours: 72, percent: 0.20 },
    { minHours: 24, maxHours: 48, percent: 0.30 },
    { minHours: 0,  maxHours: 24, percent: 0.40 }
  ],

  // Orders placed at or after this hour roll forward to the next calendar day.
  ORDER_CUTOFF_HOUR_LOCAL: 20, // 8:00 PM

  cutoffHours: 72
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const FEE_VARIANT_IDS = new Set(CONFIG.feeTiers.map(t => t.variantId));

function isFeeLine(item) {
  return FEE_VARIANT_IDS.has(item.variant_id);
}

function cartHasCatering(cart) {
  return cart.items.some(
    item => item.product_type === CONFIG.cateringProductType && !isFeeLine(item)
  );
}

function getCartSubtotalCents(cart) {
  return cart.items
    .filter(item => !isFeeLine(item))
    .reduce((sum, item) => sum + item.final_line_price, 0);
}

function getExistingFeeLines(cart) {
  return cart.items.filter(isFeeLine);
}

/**
 * Returns the "effective order time" — the moment from which 72h is measured.
 * Orders placed at or after the cutoff (8 PM) roll forward to midnight of the
 * next local calendar day.
 */
function getEffectiveOrderTime() {
  const now = new Date();
  if (now.getHours() >= CONFIG.ORDER_CUTOFF_HOUR_LOCAL) {
    const next = new Date(now);
    next.setDate(next.getDate() + 1);
    next.setHours(0, 0, 0, 0);
    return next;
  }
  return now;
}

function localStartOfDay(yyyymmdd) {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function localEndOfDay(yyyymmdd) {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999);
}

function hoursBetween(from, to) {
  return (to.getTime() - from.getTime()) / (1000 * 60 * 60);
}

function isFeeFreeDay(selectedDateStr) {
  const effective = getEffectiveOrderTime();
  const endOfSelected = localEndOfDay(selectedDateStr);
  return hoursBetween(effective, endOfSelected) >= CONFIG.cutoffHours;
}

function hoursUntilDeliveryStart(selectedDateStr) {
  const effective = getEffectiveOrderTime();
  const startOfSelected = localStartOfDay(selectedDateStr);
  return hoursBetween(effective, startOfSelected);
}

function calculateFeeCents(subtotalCents, selectedDateStr) {
  if (isFeeFreeDay(selectedDateStr)) return 0;

  const hours = hoursUntilDeliveryStart(selectedDateStr);
  if (hours < 0) return 0;

  if (subtotalCents < CONFIG.flatFeeThresholdCents) {
    return CONFIG.flatFeeAmountCents;
  }

  const tier = CONFIG.percentTiers.find(t => hours >= t.minHours && hours < t.maxHours);
  if (!tier) return 0;

  return Math.round(subtotalCents * tier.percent);
}

function pickFeeTier(feeCents) {
  if (feeCents <= 0) return null;
  let best = CONFIG.feeTiers[0];
  let bestDist = Math.abs(feeCents - best.amountCents);
  for (let i = 1; i < CONFIG.feeTiers.length; i++) {
    const t = CONFIG.feeTiers[i];
    const dist = Math.abs(feeCents - t.amountCents);
    if (dist < bestDist || (dist === bestDist && t.amountCents > best.amountCents)) {
      best = t;
      bestDist = dist;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Cart API
// ---------------------------------------------------------------------------

async function getCart() {
  const res = await fetch('/cart.js');
  return res.json();
}

async function updateCartAttributes(attributes) {
  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes })
  });
  return res.json();
}

async function addFeeProduct(variantId, displayLabel) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [{
        id: variantId,
        quantity: 1,
        properties: {
          '_rush_fee': 'true',
          '_locked': 'true',
          'Delivery date': displayLabel
        }
      }]
    })
  });
  return res.json();
}

async function removeAllFeeLines(cart) {
  const updates = {};
  cart.items.forEach(item => {
    if (isFeeLine(item)) updates[item.key] = 0;
  });
  if (Object.keys(updates).length === 0) return cart;

  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates })
  });
  return res.json();
}

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Standard delivery window (3–5 calendar days from effective order time)
// ---------------------------------------------------------------------------

/** Returns {start, end} Date objects for the standard delivery window. */
function getStandardWindow() {
  const effective = getEffectiveOrderTime();
  return {
    start: new Date(effective.getTime() + 3 * 24 * 60 * 60 * 1000),
    end:   new Date(effective.getTime() + 5 * 24 * 60 * 60 * 1000),
  };
}

/** Converts a Date to 'YYYY-MM-DD' string. */
function dateToStr(date) {
  return date.getFullYear() + '-' +
    String(date.getMonth() + 1).padStart(2, '0') + '-' +
    String(date.getDate()).padStart(2, '0');
}

/** True if dateStr falls within the 3–5-day standard window. */
function isInStandardWindow(dateStr) {
  const { start, end } = getStandardWindow();
  const [y, m, d] = dateStr.split('-').map(Number);
  const date    = new Date(y, m - 1, d);
  const dayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const dayEnd   = new Date(end.getFullYear(),   end.getMonth(),   end.getDate());
  return date >= dayStart && date <= dayEnd;
}

/** True if dateStr is beyond the 5-day window (exact-date, no rush fee). */
function isExactDateDelivery(dateStr) {
  const { end } = getStandardWindow();
  const [y, m, d] = dateStr.split('-').map(Number);
  const date   = new Date(y, m - 1, d);
  const dayEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return date > dayEnd;
}

/** "Thursday – Saturday" label for the default trigger when no date is selected. */
function getDeliveryWindowLabel() {
  const { start, end } = getStandardWindow();
  const opts = { weekday: 'long' };
  return start.toLocaleDateString('en-US', opts) + ' – ' + end.toLocaleDateString('en-US', opts);
}

function buildDeliveryLabel(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

async function reconcileRushFee() {
  let cart = await getCart();

  if (!cartHasCatering(cart)) {
    if (getExistingFeeLines(cart).length > 0) {
      cart = await removeAllFeeLines(cart);
    }
    showOrHidePicker(cart);
    return cart;
  }

  showOrHidePicker(cart);

  const dateStr = cart.attributes['delivery_date'];
  if (!dateStr) {
    if (getExistingFeeLines(cart).length > 0) {
      cart = await removeAllFeeLines(cart);
    }
    return cart;
  }

  const subtotal = getCartSubtotalCents(cart);
  const feeCents = calculateFeeCents(subtotal, dateStr);
  const targetTier = pickFeeTier(feeCents);

  const existing = getExistingFeeLines(cart);
  const existingVariantId = existing.length === 1 ? existing[0].variant_id : null;

  if (targetTier && existingVariantId === targetTier.variantId && existing.length === 1) {
    return cart;
  }

  if (existing.length > 0) {
    cart = await removeAllFeeLines(cart);
  }

  if (targetTier) {
    const label = buildDeliveryLabel(dateStr);
    cart = await addFeeProduct(targetTier.variantId, label);
  }

  return cart;
}

// ---------------------------------------------------------------------------
// Picker visibility
// ---------------------------------------------------------------------------

function showOrHidePicker(cart) {
  const wrapper = document.getElementById('CateringPicker');
  if (!wrapper) return;
  wrapper.dataset.active = cartHasCatering(cart) ? 'true' : 'false';
}

// ---------------------------------------------------------------------------
// Cart display refresh (section rendering)
// ---------------------------------------------------------------------------

function refreshCartDisplay() {
  const cartItemsSectionId = document.querySelector('#main-cart-items[data-id]')?.dataset.id;
  const cartFooterSectionId = document.querySelector('#main-cart-footer[data-id]')?.dataset.id;

  if (cartItemsSectionId) {
    fetch(`${window.location.pathname}?section_id=${cartItemsSectionId}`)
      .then(res => res.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Update cart item list
        const newCart = doc.querySelector('cart-items');
        const currentCart = document.querySelector('cart-items');
        if (newCart && currentCart) {
          currentCart.innerHTML = newCart.innerHTML;
          getCart().then(cart => {
            showOrHidePicker(cart);
            CalendarUI.setCart(cart);
          });
        }

        // Update summary card — same section HTML, no extra fetch needed.
        // (The MutationObserver approach is unreliable here because replacing
        // cart-items.innerHTML detaches the observed #main-cart-items node.)
        const newCard = doc.querySelector('.jk-summary-card');
        const curCard = document.querySelector('.jk-summary-card');
        if (newCard && curCard) curCard.innerHTML = newCard.innerHTML;

        // Update item-count heading
        const newH = doc.getElementById('jk-cart-heading');
        const curH = document.getElementById('jk-cart-heading');
        if (newH && curH) curH.textContent = newH.textContent;
      });
  }

  if (cartFooterSectionId) {
    fetch(`${window.location.pathname}?section_id=${cartFooterSectionId}`)
      .then(res => res.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newFooter = doc.querySelector('.cart__footer');
        const currentFooter = document.querySelector('.cart__footer');
        if (newFooter && currentFooter) currentFooter.innerHTML = newFooter.innerHTML;
      });
  }
}

// ============================================================================
// CALENDAR COMPONENT
// ============================================================================

const CalendarUI = {
  // State
  viewYear: null,
  viewMonth: null,
  selectedDateStr: null,
  isOpen: false,
  cart: null,

  // DOM refs
  trigger: null,
  triggerValue: null,
  calendar: null,
  monthLabel: null,
  daysGrid: null,
  prevBtn: null,
  nextBtn: null,
  hiddenInput: null,
  status: null,
  statusPill: null,
  statusText: null,

  init() {
    this.trigger = document.getElementById('CateringDateTrigger');
    this.triggerLabel = document.querySelector('.catering-picker__trigger-label');
    this.triggerValue = document.getElementById('CateringDateValue');
    this.calendar = document.getElementById('CateringCalendar');
    this.monthLabel = document.getElementById('CateringCalendarMonth');
    this.daysGrid = document.getElementById('CateringCalendarDays');
    this.prevBtn = document.getElementById('CateringCalendarPrev');
    this.nextBtn = document.getElementById('CateringCalendarNext');
    this.hiddenInput = document.getElementById('CateringDeliveryDateAttr');
    this.status = document.getElementById('CateringPickerStatus');
    this.statusPill = document.getElementById('CateringStatusPill');
    this.statusText = document.getElementById('CateringStatusText');

    if (!this.trigger || !this.calendar) return;

    const initial = this.hiddenInput.value || '';
    if (initial && /^\d{4}-\d{2}-\d{2}$/.test(initial)) {
      this.selectedDateStr = initial;
      const [y, m] = initial.split('-').map(Number);
      this.viewYear = y;
      this.viewMonth = m - 1;
    } else {
      const today = new Date();
      this.viewYear = today.getFullYear();
      this.viewMonth = today.getMonth();
    }

    this.trigger.addEventListener('click', () => this.toggle());
    this.prevBtn.addEventListener('click', () => this.shiftMonth(-1));
    this.nextBtn.addEventListener('click', () => this.shiftMonth(1));

    document.addEventListener('click', (e) => {
      if (!this.isOpen) return;
      if (this.calendar.contains(e.target)) return;
      if (this.trigger.contains(e.target)) return;
      this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    this.render();
    this.updateTriggerLabel();
    this.refreshStatus();
  },

  setCart(cart) {
    this.cart = cart;
    if (this.isOpen) this.render();
    this.refreshStatus();
  },

  toggle() {
    this.isOpen ? this.close() : this.open();
  },

  open() {
    this.calendar.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.render();
  },

  close() {
    this.calendar.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
  },

  shiftMonth(delta) {
    let m = this.viewMonth + delta;
    let y = this.viewYear;
    if (m < 0)  { m = 11; y -= 1; }
    if (m > 11) { m = 0;  y += 1; }
    this.viewYear = y;
    this.viewMonth = m;
    this.render();
  },

  render() {
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    this.monthLabel.textContent = `${monthNames[this.viewMonth]} ${this.viewYear}`;

    const today = new Date();
    const tYear = today.getFullYear();
    const tMonth = today.getMonth();
    this.prevBtn.disabled = (this.viewYear === tYear && this.viewMonth === tMonth) ||
                            (this.viewYear < tYear) ||
                            (this.viewYear === tYear && this.viewMonth < tMonth);

    this.daysGrid.innerHTML = '';
    const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
    const startWeekday = firstOfMonth.getDay();

    const prevMonthLast = new Date(this.viewYear, this.viewMonth, 0).getDate();
    for (let i = startWeekday - 1; i >= 0; i--) {
      const day = prevMonthLast - i;
      this.daysGrid.appendChild(this.buildDayCell(day, true, null));
    }

    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    const subtotal = this.cart ? getCartSubtotalCents(this.cart) : 0;
    const todayStr = this.formatDate(today);
    const todayDay = today.getDate();
    const isCurrentMonth = (this.viewYear === tYear && this.viewMonth === tMonth);

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${this.viewYear}-${String(this.viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const isPast = isCurrentMonth && d < todayDay;
      const isToday = dateStr === todayStr;
      const isSelected = dateStr === this.selectedDateStr;
      const tier = isPast ? null : this.computeTierForDate(dateStr, subtotal);
      this.daysGrid.appendChild(this.buildDayCell(d, false, tier, {
        dateStr, isPast, isToday, isSelected
      }));
    }

    const cellsSoFar = startWeekday + daysInMonth;
    const trailing = (7 - (cellsSoFar % 7)) % 7;
    for (let i = 1; i <= trailing; i++) {
      this.daysGrid.appendChild(this.buildDayCell(i, true, null));
    }
  },

  buildDayCell(dayNum, isMuted, tier, opts = {}) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'catering-calendar__day';
    btn.textContent = dayNum;

    if (isMuted) {
      btn.dataset.muted = 'true';
      return btn;
    }

    if (opts.isPast) {
      btn.disabled = true;
    } else {
      btn.dataset.tier = tier;
      btn.addEventListener('click', () => this.selectDate(opts.dateStr));
    }

    if (opts.isToday) btn.dataset.today = 'true';
    if (opts.isSelected) btn.dataset.selected = 'true';

    if (tier) {
      const dot = document.createElement('span');
      dot.className = 'catering-calendar__day-dot';
      btn.appendChild(dot);
    }

    return btn;
  },

  computeTierForDate(dateStr, subtotalCents) {
    if (isFeeFreeDay(dateStr)) return 'free';
    const hours = hoursUntilDeliveryStart(dateStr);
    if (hours < 0) return 'heavy';
    if (hours >= 48) return 'light';
    if (hours >= 24) return 'medium';
    return 'heavy';
  },

  async selectDate(dateStr) {
    this.selectedDateStr = dateStr;
    this.updateTriggerLabel();
    this.close();

    // Build cart attributes that will surface as order attributes post-checkout
    const attrs = { 'delivery_date': dateStr };
    if (isInStandardWindow(dateStr)) {
      const { start, end } = getStandardWindow();
      attrs['delivery_window'] = buildDeliveryLabel(dateToStr(start)) + ' – ' + buildDeliveryLabel(dateToStr(end));
      attrs['delivery_type']   = 'Standard window (3–5 days)';
    } else if (isExactDateDelivery(dateStr)) {
      attrs['delivery_window'] = '';
      attrs['delivery_type']   = 'Exact date: ' + buildDeliveryLabel(dateStr);
    } else {
      attrs['delivery_window'] = '';
      attrs['delivery_type']   = 'Rush delivery: ' + buildDeliveryLabel(dateStr);
    }

    await updateCartAttributes(attrs);
    await reconcileRushFee();
    refreshCartDisplay();

    this.cart = await getCart();
    this.refreshStatus();
  },

  updateTriggerLabel() {
    // No date selected, or a date in the standard window — always show the range
    if (!this.selectedDateStr || isInStandardWindow(this.selectedDateStr)) {
      if (this.triggerLabel) this.triggerLabel.textContent = 'Estimated delivery';
      this.triggerValue.textContent = getDeliveryWindowLabel();
      return;
    }
    // Exact date beyond window, or rush date — show the specific day
    if (this.triggerLabel) this.triggerLabel.textContent = 'Delivery date';
    const [y, m, d] = this.selectedDateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    this.triggerValue.textContent = dt.toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric'
    });
  },

  formatDate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  },

  refreshStatus() {
    if (!this.selectedDateStr || !this.cart) {
      this.status.hidden = true;
      return;
    }

    delete this.status.dataset.state;

    const subtotal  = getCartSubtotalCents(this.cart);
    const feeCents  = calculateFeeCents(subtotal, this.selectedDateStr);
    const tier      = this.computeTierForDate(this.selectedDateStr, subtotal);
    const [y, m, d] = this.selectedDateStr.split('-').map(Number);
    const dt        = new Date(y, m - 1, d);
    const dateLabel = dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

    this.status.hidden = false;
    this.statusPill.dataset.tier = tier;
    const pillText = this.statusPill.querySelector('.catering-picker__status-pill-text');

    if (isInStandardWindow(this.selectedDateStr)) {
      // Date is within the 3–5-day standard window
      const { start, end } = getStandardWindow();
      const startLabel = start.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
      const endLabel   = end.toLocaleDateString('en-US',   { weekday: 'long', month: 'long', day: 'numeric' });
      pillText.textContent = 'Standard delivery';
      this.statusText.innerHTML =
        `Your order will be delivered between <strong>${startLabel}</strong> and <strong>${endLabel}</strong>. ` +
        `If you need it on a specific day within that range, add a note in the order instructions.`;
    } else if (feeCents === 0) {
      // Date is beyond 5 days — exact scheduled delivery, no fee
      pillText.textContent = 'Standard delivery';
      this.statusText.innerHTML = `Your order will be delivered on <strong>${dateLabel}</strong>. Delivery fee determined at checkout.`;
    } else {
      // Date is within 3 days — rush fee applies
      const targetTier = pickFeeTier(feeCents);
      const charged = targetTier ? (targetTier.amountCents / 100).toFixed(0) : (feeCents / 100).toFixed(0);
      pillText.textContent = 'Rush delivery';
      this.statusText.innerHTML = `A <strong>$${charged} rush fee</strong> will be added for delivery on <strong>${dateLabel}</strong>.`;
    }
  }
};

// ---------------------------------------------------------------------------
// Cart change listeners
// ---------------------------------------------------------------------------

document.addEventListener('cart:refresh', async () => {
  const cart = await reconcileRushFee();
  CalendarUI.setCart(cart);
  refreshCartDisplay();
});

document.addEventListener('change', (e) => {
  if (e.target.matches('cart-items input[name="updates[]"], cart-items quantity-input input')) {
    setTimeout(async () => {
      const cart = await reconcileRushFee();
      CalendarUI.setCart(cart);
      refreshCartDisplay();
    }, 300);
  }
});

// ---------------------------------------------------------------------------
// Checkout guard
// ---------------------------------------------------------------------------

function initCheckoutGuard() {
  const checkoutBtn = document.querySelector('[name="checkout"]');
  if (!checkoutBtn) return;

  checkoutBtn.addEventListener('click', async (e) => {
    const cart = await getCart();
    if (!cartHasCatering(cart)) return;

    if (!cart.attributes['delivery_date']) {
      e.preventDefault();
      const status = document.getElementById('CateringPickerStatus');
      const statusPill = document.getElementById('CateringStatusPill');
      const statusText = document.getElementById('CateringStatusText');
      if (status) {
        status.hidden = false;
        status.dataset.state = 'error';
        const pillText = statusPill.querySelector('.catering-picker__status-pill-text');
        pillText.textContent = 'Required';
        statusText.textContent = 'Please select a delivery date for your catering order.';
      }
      document.getElementById('CateringDateTrigger')?.focus();
      document.getElementById('CateringPicker')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

async function init() {
  if (!document.getElementById('CateringPicker')) return;
  CalendarUI.init();
  initCheckoutGuard();

  // Subscribe to Dawn's pubsub so cart-page "Remove" button also triggers reconciliation
  try {
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, async () => {
        const cart = await reconcileRushFee();
        CalendarUI.setCart(cart);
      });
    }
  } catch (e) { /* ignore — pubsub may not be available on all pages */ }

  const cart = await reconcileRushFee();
  CalendarUI.setCart(cart);
  showOrHidePicker(cart);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
